import { Model } from "@nozbe/watermelondb";
import { useMemo, useState } from "react";
import { useDatabase, useFetchAndSyncDatabaseEntities } from "../data/hooks";
import FetchMeta from "../data/model/FetchMeta";
import { allStartupDefinitions as definitions } from "./definitions/definitions";
import { SyncProgressEvent, SyncProgressStatus } from "./page-query";

export const useSetupAllStartupEntities = () => {
  const [syncing, setsyncing] = useState(false);
  const { fetchEntity } = useFetchAndSyncDatabaseEntities();
  const database = useDatabase();

  function makeInitialProgressState() {
    return Object.fromEntries(
      definitions.map(({ config }) => [
        config.entity,
        {
          fetched: 0,
          items: [],
          synced: 0,
          status: SyncProgressStatus.PENDING,
          totalItems: 0,
          estimatedTotalItems: 0,
        },
      ])
    );
  }

  const initialProgressState = useMemo(makeInitialProgressState, []);

  const [progressState, setProgressState] = useState<{
    [key: string]: SyncProgressEvent;
  }>(initialProgressState);

  const initializeAllEntitiesFetchData = async () => {
    const batchActions: Model[] = [];

    for (const definition of definitions) {
      let maybeExistingSyncMeta: FetchMeta | undefined;
      try {
        maybeExistingSyncMeta = await database
          .get<FetchMeta>("fetch_meta")
          .find(definition.config.entity);
      } catch (e) {
        console.log(e);
      }
      if (!maybeExistingSyncMeta) {
        batchActions.push(
          database.get<FetchMeta>("fetch_meta").prepareCreate((newEntity) => {
            newEntity._raw.id = definition.config.entity;
          })
        );
      }
    }

    await database.write(async () => {
      await database.batch(batchActions);
    });
  };

  const fetchAndSyncAllEntities = async () => {
    setsyncing(true);
    setProgressState(initialProgressState);
    await initializeAllEntitiesFetchData();
    for (const definition of definitions) {
      try {
        await fetchEntity(
          definition.config.entity,
          definition.config.fetchEntities as any,
          definition.config.prepareEntity as any,
          (event: Partial<SyncProgressEvent>) => {
            setProgressState((prev) => ({
              ...prev,
              [definition.config.entity]: {
                ...(prev[definition.config.entity] ?? {
                  fetched: 0,
                  items: [],
                  status: SyncProgressStatus.PENDING,
                  totalItems: 0,
                }),
                ...event,
              },
            }));
          }
        );
      } catch (e) {
        console.log(e);
      }
    }

    setsyncing(false);
  };

  const fetchAndSyncEntity = async (entity: string) => {
    setsyncing(true);
    const definition = definitions.find((d) => d.config.entity === entity);
    if (definition) {
      try {
        await fetchEntity(
          definition.config.entity,
          definition.config.fetchEntities as any,
          definition.config.prepareEntity as any
        );
      } catch (e) {
        console.log(e);
      }
    }
    setsyncing(false);
  };

  return {
    syncing,
    progressState,
    fetchAndSyncAllEntities,
    fetchAndSyncEntity,
    initializeAllEntitiesFetchData,
  };
};
